/* Hero slider — sections/hero-slider.liquid */

if (!customElements.get('hero-slider')) {
  class HeroSlider extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('[data-hero-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.prevButton = this.querySelector('[data-hero-prev]');
      this.nextButton = this.querySelector('[data-hero-next]');
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.autoplaySpeed = (parseInt(this.dataset.speed, 10) || 6) * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    connectedCallback() {
      if (!this.track || this.slides.length === 0) return;

      this.onScroll = this.debounce(this.handleScroll.bind(this), 100);
      this.track.addEventListener('scroll', this.onScroll);

      this.viewportWidth = window.innerWidth;
      this.onResize = this.debounce(this.handleResize.bind(this), 150);
      window.addEventListener('resize', this.onResize);

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this.pauseAutoplay();
          this.goTo(this.currentIndex - 1);
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.pauseAutoplay();
          this.goTo(this.currentIndex + 1);
        });
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.pauseAutoplay();
          this.goTo(index);
        });
      });

      this.addEventListener('keydown', this.handleKeydown.bind(this));

      if (this.autoplayEnabled) {
        this.addEventListener('mouseenter', this.pauseAutoplay.bind(this));
        this.addEventListener('mouseleave', this.resumeAutoplay.bind(this));
        this.addEventListener('focusin', this.pauseAutoplay.bind(this));
        this.addEventListener('focusout', this.resumeAutoplay.bind(this));
        this.track.addEventListener('touchstart', this.pauseAutoplay.bind(this), { passive: true });
        document.addEventListener('visibilitychange', this.handleVisibility.bind(this));
        this.startAutoplay();
      }

      this.update(0);
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    handleScroll() {
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (index !== this.currentIndex) this.update(index);
    }

    /* Le scroll-snap ne se recale pas toujours seul quand la largeur change */
    handleResize() {
      if (window.innerWidth === this.viewportWidth) return;
      this.viewportWidth = window.innerWidth;
      this.track.scrollTo({ left: this.track.clientWidth * this.currentIndex, behavior: 'instant' });
    }

    handleKeydown(event) {
      if (this.slides.length < 2) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.pauseAutoplay();
        this.goTo(this.currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.pauseAutoplay();
        this.goTo(this.currentIndex + 1);
      }
    }

    handleVisibility() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    goTo(index) {
      const total = this.slides.length;
      const target = ((index % total) + total) % total;
      this.track.scrollTo({
        left: this.track.clientWidth * target,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.update(target);
    }

    update(index) {
      this.currentIndex = index;

      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        if (this.slides.length > 1) slide.setAttribute('aria-hidden', String(!isActive));
      });

      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.reducedMotion.matches || this.autoplayPaused) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => this.goTo(this.currentIndex + 1), this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    pauseAutoplay() {
      this.autoplayPaused = true;
      this.stopAutoplay();
    }

    resumeAutoplay() {
      this.autoplayPaused = false;
      this.startAutoplay();
    }
  }

  customElements.define('hero-slider', HeroSlider);
}

/* Éditeur de thème : afficher l'image sélectionnée */
document.addEventListener('shopify:block:select', (event) => {
  const slider = event.target.closest('hero-slider');
  if (!slider || !event.target.hasAttribute('data-hero-slide')) return;
  const slides = Array.from(slider.querySelectorAll('[data-hero-slide]'));
  slider.pauseAutoplay();
  slider.goTo(slides.indexOf(event.target));
});

document.addEventListener('shopify:block:deselect', (event) => {
  const slider = event.target.closest('hero-slider');
  if (slider) slider.resumeAutoplay();
});
